import { useEffect, useId, useRef, useState, type DragEvent } from "react";
import { useObjectURL } from "@/components/useObjectURL";

/** "212 KB" or "4.3 MB", the way the size limits are written. */
export function formatBytes(n: number) {
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")} MB`;
}

/**
 * A drop zone around the native file input. The input stays in the page (visually hidden), so Tab,
 * Enter and Space open the browser's picker and screen readers announce it by its label.
 */
export function FilePicker({
  label,
  accept,
  extensions,
  typeError,
  maxBytes,
  hint,
  value,
  onChange,
  thumbnail = false,
}: {
  label: string;
  /** The input's accept attribute, e.g. ".ttf,font/ttf". */
  accept: string;
  /** Lower-case extensions a file name must end with, e.g. [".png", ".jpg", ".jpeg"]. */
  extensions: string[];
  typeError: string;
  maxBytes: number;
  hint?: string;
  value: File | null;
  onChange: (file: File | null) => void;
  /** Image uploads show the chosen file as a thumbnail. */
  thumbnail?: boolean;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const preview = useObjectURL(thumbnail && value ? value : undefined);

  // The parent clears its file after an upload; clear the input so the same file can be picked again.
  useEffect(() => {
    if (!value && input.current) input.current.value = "";
  }, [value]);

  const take = (files: FileList | File[] | null | undefined) => {
    const list = Array.from(files ?? []);
    const file = list[0];
    if (!file) return;
    setNote(list.length > 1 ? "Only the first file was kept." : null);
    if (!extensions.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      setProblem(typeError);
      return;
    }
    if (file.size > maxBytes) {
      setProblem(`Larger than ${formatBytes(maxBytes)}`);
      return;
    }
    setProblem(null);
    onChange(file);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    take(e.dataTransfer?.files);
  };

  const hintId = `${id}-hint`;
  const problemId = `${id}-problem`;
  return (
    <div className="text-sm">
      <label htmlFor={id} className="font-medium text-zinc-700">
        {label}
      </label>
      <input
        ref={input}
        id={id}
        type="file"
        accept={accept}
        className="peer sr-only"
        aria-describedby={[hint ? hintId : "", problem ? problemId : ""].filter(Boolean).join(" ") || undefined}
        onChange={(e) => take(e.target.files)}
      />
      <div
        data-testid="file-drop-zone"
        data-dragging={dragging}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`mt-1 rounded-lg border-2 transition peer-focus-visible:ring-2 peer-focus-visible:ring-brand ${
          dragging
            ? "border-solid border-brand bg-brand-light/25"
            : value
              ? "border-solid border-brand/40 bg-white"
              : "border-dashed border-brand/40 bg-brand-light/10 hover:bg-brand-light/20"
        }`}
      >
        {value ? (
          <div className="flex flex-wrap items-center gap-3 p-3">
            {preview ? (
              <img src={preview} alt="" className="h-12 w-12 rounded border border-zinc-200 object-contain" />
            ) : (
              <span aria-hidden="true" className="grid h-10 w-10 place-items-center rounded bg-brand-light/30 text-brand-ink">
                <FileIcon />
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-zinc-900">{value.name}</p>
              <p className="text-xs text-zinc-500">{formatBytes(value.size)}</p>
            </div>
            <button
              type="button"
              aria-label="Change file"
              onClick={() => input.current?.click()}
              className="rounded-md border border-brand/40 bg-brand-light/15 px-3 py-1.5 text-xs font-medium text-brand-ink hover:bg-brand-light/30"
            >
              Change
            </button>
            <button
              type="button"
              aria-label={`Remove ${value.name}`}
              onClick={() => {
                setNote(null);
                onChange(null);
              }}
              className="rounded-md px-2 py-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
            >
              ✕
            </button>
          </div>
        ) : (
          <label htmlFor={id} className="flex cursor-pointer flex-col items-center gap-2 px-4 py-6 text-center">
            <span aria-hidden="true" className="text-brand">
              <UploadIcon />
            </span>
            <span className="text-zinc-700">
              Drag a file here, or{" "}
              <span className="inline-block rounded-md bg-brand-strong px-3 py-1.5 font-medium text-white">Choose file</span>
            </span>
            {hint ? (
              <span id={hintId} className="text-xs text-zinc-500">
                {hint}
              </span>
            ) : null}
          </label>
        )}
      </div>
      {value && hint ? (
        <p id={hintId} className="mt-1 text-xs text-zinc-500">
          {hint}
        </p>
      ) : null}
      {problem ? (
        <p id={problemId} role="alert" className="mt-1 text-xs text-red-700">
          {problem}
        </p>
      ) : null}
      {note ? <p className="mt-1 text-xs text-zinc-500">{note}</p> : null}
    </div>
  );
}

function UploadIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 16V4M7 9l5-5 5 5" />
      <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7z" />
      <path d="M14 3v4h4" />
    </svg>
  );
}
